// src/pages/offers-stats/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { supabase } from "../../utils/supabase";

/* ─── Constantes de UI ─── */
const STATUS_LABELS = {
  draft: "Borrador",
  sent: "Enviada",
  accepted: "Aceptada",
  billing: "Facturando",
  closed: "Cerrada",
  rejected: "Rechazada",
};

const STATUS_COLORS = {
  draft: "#94a3b8",
  sent: "#60a5fa",
  accepted: "#34d399",
  billing: "#f59e0b",
  closed: "#6b7280",
  rejected: "#f87171",
};

const FUNNEL_ORDER = ["draft", "sent", "accepted", "billing", "closed"];

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

/* ─── Helpers ─── */
const fmt = (n, decimals = 0) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

// Símbolo según moneda
const currencySymbol = (cur) => {
  if (!cur) return "$";
  const c = cur.toUpperCase();
  if (c === "USD") return "U$D ";
  if (c === "EUR") return "€";
  return "$";
};

const fmtMoney = (n, cur) => `${currencySymbol(cur)}${fmt(n, 2)}`;

const isoToMonthKey = (iso) => {
  if (!iso) return null;
  return String(iso).split("T")[0].slice(0, 7); // YYYY-MM
};

/* ─── Componente KPI Card ─── */
function KpiCard({ label, value, sub, color = "text-foreground" }) {
  return (
    <div className="card rounded-xl p-4 flex flex-col gap-1 border border-white/10">
      <span className="text-xs text-muted uppercase tracking-wide">{label}</span>
      <span className={`text-2xl font-bold ${color}`}>{value}</span>
      {sub && <span className="text-xs text-muted">{sub}</span>}
    </div>
  );
}

/* ─── Tooltip personalizado ─── */
function CustomTooltip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-zinc-900 p-3 text-xs shadow-xl">
      <div className="mb-1 font-semibold text-foreground">{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }} className="flex justify-between gap-4">
          <span>{p.name}</span>
          <span className="font-mono">
            {p.name?.toLowerCase().includes("monto") || p.name?.toLowerCase().includes("total")
              ? fmtMoney(p.value, currency)
              : fmt(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Selector de moneda (tabs) ─── */
function CurrencyTabs({ currencies, value, onChange }) {
  return (
    <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 p-1">
      <button
        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
          value === "all"
            ? "bg-white/15 text-foreground"
            : "text-muted hover:text-foreground"
        }`}
        onClick={() => onChange("all")}
      >
        Todas
      </button>
      {currencies.map((cur) => (
        <button
          key={cur}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            value === cur
              ? "bg-white/15 text-foreground"
              : "text-muted hover:text-foreground"
          }`}
          onClick={() => onChange(cur)}
        >
          {cur}
        </button>
      ))}
    </div>
  );
}

/* ─── Página principal ─── */
export default function OffersStatsPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");

  /* ── Carga de datos ── */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        let allOffers = [];
        let from = 0;
        const pageSize = 1000;
        while (true) {
          const { data, error: err } = await supabase
            .from("offers")
            .select(
              "id,base_number,code,status,subtotal,total,price_currency,created_at,client:clients!offers_client_id_fkey(id,name)"
            )
            .order("created_at", { ascending: true })
            .range(from, from + pageSize - 1);

          if (err) throw err;
          allOffers = allOffers.concat(data ?? []);
          if (!data || data.length < pageSize) break;
          from += pageSize;
        }
        setOffers(allOffers);
      } catch (e) {
        console.error("[OffersStats] load error:", e);
        setError("No se pudieron cargar los datos.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ── Monedas disponibles ── */
  const availableCurrencies = useMemo(() => {
    const set = new Set(offers.map((o) => o.price_currency || "ARS").filter(Boolean));
    return Array.from(set).sort();
  }, [offers]);

  /* ── Años disponibles ── */
  const availableYears = useMemo(() => {
    const years = new Set(
      offers.map((o) => String(o.created_at || "").slice(0, 4)).filter(Boolean)
    );
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [offers]);

  /* ── Ofertas filtradas ── */
  const filtered = useMemo(() => {
    return offers.filter((o) => {
      const yearOk = yearFilter === "all" || String(o.created_at || "").startsWith(yearFilter);
      const cur = o.price_currency || "ARS";
      const currencyOk = currencyFilter === "all" || cur === currencyFilter;
      return yearOk && currencyOk;
    });
  }, [offers, yearFilter, currencyFilter]);

  /* ── Moneda activa para formateo ── */
  // Si hay una moneda seleccionada, usarla; si es "all" y solo hay una, usarla también
  const activeCurrency = useMemo(() => {
    if (currencyFilter !== "all") return currencyFilter;
    if (availableCurrencies.length === 1) return availableCurrencies[0];
    return null; // mixto
  }, [currencyFilter, availableCurrencies]);

  /* ── KPIs ── */
  const kpis = useMemo(() => {
    const total = filtered.length;
    const totalAmount = filtered.reduce((s, o) => s + Number(o.subtotal || 0), 0);
    const accepted = filtered.filter((o) => ["accepted", "billing", "closed"].includes(o.status)).length;
    const rejected = filtered.filter((o) => o.status === "rejected").length;
    const sent = filtered.filter((o) => o.status === "sent").length;
    const avgValue = total > 0 ? totalAmount / total : 0;
    const acceptanceRate = (total - rejected > 0)
      ? (accepted / (total - rejected)) * 100
      : 0;
    return { total, totalAmount, accepted, rejected, sent, avgValue, acceptanceRate };
  }, [filtered]);

  /* ── Por estado ── */
  const byStatus = useMemo(() => {
    const counts = {};
    filtered.forEach((o) => {
      counts[o.status] = (counts[o.status] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([status, count]) => ({
        status,
        label: STATUS_LABELS[status] || status,
        count,
        color: STATUS_COLORS[status] || "#6b7280",
      }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  /* ── Tendencia mensual ── */
  const monthlyTrend = useMemo(() => {
    const map = {};
    filtered.forEach((o) => {
      const key = isoToMonthKey(o.created_at);
      if (!key) return;
      if (!map[key]) map[key] = { key, ofertas: 0, montoNeto: 0, aceptadas: 0 };
      map[key].ofertas++;
      map[key].montoNeto += Number(o.subtotal || 0);
      if (["accepted", "billing", "closed"].includes(o.status)) map[key].aceptadas++;
    });

    return Object.values(map)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((row) => ({
        ...row,
        mes: (() => {
          const [y, m] = row.key.split("-");
          return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y.slice(2)}`;
        })(),
      }));
  }, [filtered]);

  /* ── Aceptadas mensual por moneda ── */
  const monthlyAccepted = useMemo(() => {
    const accepted = offers.filter((o) => {
      if (!["accepted", "billing", "closed"].includes(o.status)) return false;
      const yearOk = yearFilter === "all" || String(o.created_at || "").startsWith(yearFilter);
      return yearOk;
    });

    const map = {};
    accepted.forEach((o) => {
      const key = isoToMonthKey(o.created_at);
      if (!key) return;
      const cur = o.price_currency || "ARS";
      if (!map[key]) map[key] = { key, ARS: 0, USD: 0 };
      map[key][cur] = (map[key][cur] || 0) + Number(o.subtotal || 0);
    });

    return Object.values(map)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((row) => {
        const [y, m] = row.key.split("-");
        return { ...row, mes: `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y.slice(2)}` };
      });
  }, [offers, yearFilter]);

  /* ── Curva de crecimiento acumulado ── */
  const growthCurve = useMemo(() => {
    const sorted = [...filtered].sort((a, b) =>
      String(a.created_at || "").localeCompare(String(b.created_at || ""))
    );
    let cumOffers = 0;
    let cumMonto = 0;
    let cumAceptadas = 0;
    const map = {};
    sorted.forEach((o) => {
      const key = isoToMonthKey(o.created_at);
      if (!key) return;
      cumOffers++;
      cumMonto += Number(o.subtotal || 0);
      if (["accepted", "billing", "closed"].includes(o.status)) cumAceptadas++;
      map[key] = { key, cumOffers, cumMonto, cumAceptadas };
    });
    return Object.values(map)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((row) => {
        const [y, m] = row.key.split("-");
        return { ...row, mes: `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y.slice(2)}` };
      });
  }, [filtered]);

  /* ── Top 10 clientes por monto ── */
  const topClients = useMemo(() => {
    const map = {};
    filtered.forEach((o) => {
      const name = o.client?.name || "Sin cliente";
      if (!map[name]) map[name] = { nombre: name, monto: 0, cantidad: 0 };
      map[name].monto += Number(o.subtotal || 0);
      map[name].cantidad++;
    });
    return Object.values(map)
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 10);
  }, [filtered]);

  /* ── Funnel de conversión ── */
  const funnel = useMemo(() => {
    const total = filtered.length;
    return FUNNEL_ORDER.map((status) => {
      const count = filtered.filter((o) =>
        status === "accepted"
          ? ["accepted", "billing", "closed"].includes(o.status)
          : o.status === status
      ).length;
      const pct = total > 0 ? (count / total) * 100 : 0;
      return { label: STATUS_LABELS[status], count, pct, color: STATUS_COLORS[status] };
    });
  }, [filtered]);

  /* ── Resumen por moneda (siempre sobre todas las ofertas filtradas por año) ── */
  const byCurrency = useMemo(() => {
    const byYear = yearFilter === "all"
      ? offers
      : offers.filter((o) => String(o.created_at || "").startsWith(yearFilter));
    const map = {};
    byYear.forEach((o) => {
      const cur = o.price_currency || "ARS";
      if (!map[cur]) map[cur] = { currency: cur, monto: 0, cantidad: 0 };
      map[cur].monto += Number(o.subtotal || 0);
      map[cur].cantidad++;
    });
    return Object.values(map).sort((a, b) => b.cantidad - a.cantidad);
  }, [offers, yearFilter]);

  /* ── Formateo condicional según filtro ── */
  const fmtAmount = (n) =>
    activeCurrency
      ? fmtMoney(n, activeCurrency)
      : `$${fmt(n, 0)} ⚠️`;

  /* ── Render ── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted">
        Cargando estadísticas…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-rose-400">
        {error}
      </div>
    );
  }

  const mixedCurrencies = currencyFilter === "all" && availableCurrencies.length > 1;

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Encabezado */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                Estadísticas de Ofertas
              </h1>
              <p className="text-sm text-muted">
                {fmt(filtered.length)} oferta{filtered.length !== 1 ? "s" : ""}
                {currencyFilter !== "all" ? ` · ${currencyFilter}` : ""}
                {yearFilter !== "all" ? ` · ${yearFilter}` : ""}
              </p>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Filtro moneda */}
              {availableCurrencies.length > 1 && (
                <CurrencyTabs
                  currencies={availableCurrencies}
                  value={currencyFilter}
                  onChange={setCurrencyFilter}
                />
              )}

              {/* Filtro año */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted">Año:</span>
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="field-lg text-sm py-1.5 px-3 rounded-md"
                >
                  <option value="all">Todos</option>
                  {availableYears.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Aviso si hay mezcla de monedas */}
          {mixedCurrencies && (
            <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs text-amber-300 flex items-center gap-2">
              <span>⚠️</span>
              <span>
                Hay ofertas en <strong>{availableCurrencies.join(" y ")}</strong>. Los montos NO son comparables entre sí.
                Seleccioná una moneda para ver importes correctos.
              </span>
            </div>
          )}

          {/* ─── Resumen por moneda (siempre visible si hay más de una) ─── */}
          {availableCurrencies.length > 1 && (
            <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {byCurrency.map((c) => (
                <button
                  key={c.currency}
                  onClick={() => setCurrencyFilter(currencyFilter === c.currency ? "all" : c.currency)}
                  className={`card rounded-xl p-3 border text-left transition-colors cursor-pointer ${
                    currencyFilter === c.currency
                      ? "border-primary/50 bg-primary/10"
                      : "border-white/10 hover:border-white/20"
                  }`}
                >
                  <div className="text-xs text-muted mb-1 flex items-center justify-between">
                    <span className="font-mono font-semibold text-foreground">{c.currency}</span>
                    <span>{fmt(c.cantidad)} ofertas</span>
                  </div>
                  <div className="text-lg font-bold text-emerald-400">
                    {fmtMoney(c.monto, c.currency)}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* ─── KPIs ─── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            <KpiCard
              label="Total Ofertas"
              value={fmt(kpis.total)}
              color="text-foreground"
            />
            <KpiCard
              label={`Monto Neto${activeCurrency ? ` (${activeCurrency})` : ""}`}
              value={mixedCurrencies ? "—" : fmtMoney(kpis.totalAmount, activeCurrency)}
              sub={mixedCurrencies ? "Filtrá por moneda" : "suma de subtotales"}
              color={mixedCurrencies ? "text-muted" : "text-emerald-400"}
            />
            <KpiCard
              label="Aceptadas"
              value={fmt(kpis.accepted)}
              sub={`Tasa: ${fmt(kpis.acceptanceRate, 1)}%`}
              color="text-emerald-300"
            />
            <KpiCard
              label="Rechazadas"
              value={fmt(kpis.rejected)}
              color="text-rose-400"
            />
            <KpiCard
              label="En proceso"
              value={fmt(kpis.sent)}
              sub="estado Enviada"
              color="text-sky-400"
            />
            <KpiCard
              label={`Valor Promedio${activeCurrency ? ` (${activeCurrency})` : ""}`}
              value={mixedCurrencies ? "—" : fmtMoney(kpis.avgValue, activeCurrency)}
              sub={mixedCurrencies ? "Filtrá por moneda" : "por oferta"}
              color={mixedCurrencies ? "text-muted" : "text-amber-400"}
            />
          </div>

          {/* ─── Curva de crecimiento acumulado ─── */}
          <div className="card rounded-xl p-4 border border-white/10 mb-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-foreground">
                Curva de Crecimiento Acumulado
              </h2>
              {mixedCurrencies && (
                <span className="text-xs text-amber-400">⚠️ Monto mezcla {availableCurrencies.join(" + ")}</span>
              )}
            </div>
            <p className="text-xs text-muted mb-4">
              Total acumulado de ofertas y monto neto desde el primer registro
            </p>
            {growthCurve.length === 0 ? (
              <p className="text-muted text-xs">Sin datos para el período.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={growthCurve} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="mes"
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    width={32}
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10, fill: mixedCurrencies ? "#f59e0b88" : "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    width={68}
                    tickFormatter={(v) =>
                      activeCurrency === "USD"
                        ? `U$D ${(v / 1000).toFixed(0)}k`
                        : `$${(v / 1000).toFixed(0)}k`
                    }
                  />
                  <Tooltip content={<CustomTooltip currency={activeCurrency} />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#9ca3af" }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="cumOffers"
                    name="Ofertas acumuladas"
                    stroke="#60a5fa"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="cumAceptadas"
                    name="Aceptadas acumuladas"
                    stroke="#34d399"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cumMonto"
                    name="Monto acumulado"
                    stroke={mixedCurrencies ? "#f59e0b88" : "#f59e0b"}
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray={mixedCurrencies ? "4 2" : undefined}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ─── Ingresos por mes: ofertas aceptadas ARS vs USD ─── */}
          <div className="card rounded-xl p-4 border border-white/10 mb-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-foreground">
                Ingresos Mensuales — ARS vs USD
              </h2>
              <span className="text-xs text-muted">
                Ofertas aceptadas · acepta como proxy de facturación
              </span>
            </div>
            <p className="text-xs text-muted mb-4">
              Monto neto de las ofertas aceptadas (incluye: Aceptada, Facturando, Cerrada) agrupadas por mes
            </p>
            {monthlyAccepted.length === 0 ? (
              <p className="text-muted text-xs py-4">Sin ofertas aceptadas en el período.</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyAccepted} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="ars"
                      tick={{ fontSize: 10, fill: "#86efac" }}
                      axisLine={false}
                      tickLine={false}
                      width={80}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      yAxisId="usd"
                      orientation="right"
                      tick={{ fontSize: 10, fill: "#93c5fd" }}
                      axisLine={false}
                      tickLine={false}
                      width={80}
                      tickFormatter={(v) => `U$D ${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#18181b",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(value, name) => [
                        name === "ARS" ? `$${fmt(value, 2)}` : `U$D ${fmt(value, 2)}`,
                        name,
                      ]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#9ca3af" }} />
                    <Bar yAxisId="ars" dataKey="ARS" name="ARS" fill="#86efac" radius={[3,3,0,0]} maxBarSize={40} />
                    <Bar yAxisId="usd" dataKey="USD" name="USD" fill="#60a5fa" radius={[3,3,0,0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-3 flex flex-wrap gap-6 border-t border-white/10 pt-3">
                  {["ARS", "USD"].map((cur) => {
                    const total = monthlyAccepted.reduce((s, r) => s + (r[cur] || 0), 0);
                    if (total === 0) return null;
                    return (
                      <div key={cur} className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ background: cur === "ARS" ? "#86efac" : "#60a5fa" }} />
                        <span className="text-xs text-muted">Total {cur}:</span>
                        <span className="text-sm font-bold text-foreground font-mono">
                          {cur === "ARS" ? `$${fmt(total, 2)}` : `U$D ${fmt(total, 2)}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* ─── Fila 1: Tendencia mensual + Distribución por estado ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">

            {/* Tendencia mensual */}
            <div className="lg:col-span-2 card rounded-xl p-4 border border-white/10">
              <h2 className="text-sm font-semibold text-foreground mb-1">
                Tendencia Mensual — Ofertas y Monto Neto
              </h2>
              {mixedCurrencies && (
                <p className="text-xs text-amber-400 mb-3">
                  ⚠️ Línea de monto mezcla {availableCurrencies.join(" + ")} — seleccioná una moneda para ver el eje derecho correctamente.
                </p>
              )}
              {monthlyTrend.length === 0 ? (
                <p className="text-muted text-xs mt-4">Sin datos para el período.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthlyTrend} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      axisLine={false}
                      tickLine={false}
                      width={28}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 10, fill: mixedCurrencies ? "#f59e0b88" : "#9ca3af" }}
                      axisLine={false}
                      tickLine={false}
                      width={64}
                      tickFormatter={(v) =>
                        activeCurrency === "USD"
                          ? `U$D ${(v / 1000).toFixed(0)}k`
                          : `$${(v / 1000).toFixed(0)}k`
                      }
                    />
                    <Tooltip content={<CustomTooltip currency={activeCurrency} />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#9ca3af" }} />
                    <Bar
                      yAxisId="left"
                      dataKey="ofertas"
                      name="Ofertas"
                      fill="#60a5fa"
                      radius={[3, 3, 0, 0]}
                      maxBarSize={32}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="aceptadas"
                      name="Aceptadas"
                      fill="#34d399"
                      radius={[3, 3, 0, 0]}
                      maxBarSize={32}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="montoNeto"
                      name="Monto Neto"
                      stroke={mixedCurrencies ? "#f59e0b88" : "#f59e0b"}
                      strokeWidth={2}
                      dot={false}
                      strokeDasharray={mixedCurrencies ? "4 2" : undefined}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Distribución por estado */}
            <div className="card rounded-xl p-4 border border-white/10 flex flex-col">
              <h2 className="text-sm font-semibold text-foreground mb-4">
                Distribución por Estado
              </h2>
              {byStatus.length === 0 ? (
                <p className="text-muted text-xs">Sin datos.</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={byStatus}
                        dataKey="count"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={78}
                        paddingAngle={3}
                      >
                        {byStatus.map((entry) => (
                          <Cell key={entry.status} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [fmt(value), name]}
                        contentStyle={{
                          background: "#18181b",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-2 space-y-1">
                    {byStatus.map((s) => (
                      <div key={s.status} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                          <span className="text-muted">{s.label}</span>
                        </div>
                        <span className="font-mono text-foreground">{fmt(s.count)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ─── Fila 2: Top clientes + Funnel ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

            {/* Top 10 clientes */}
            <div className="card rounded-xl p-4 border border-white/10">
              <h2 className="text-sm font-semibold text-foreground mb-1">
                Top 10 Clientes por Monto Neto
              </h2>
              {mixedCurrencies && (
                <p className="text-xs text-amber-400 mb-3">
                  ⚠️ Montos en monedas distintas — seleccioná una para comparar correctamente.
                </p>
              )}
              {topClients.length === 0 ? (
                <p className="text-muted text-xs mt-4">Sin datos.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={topClients}
                    layout="vertical"
                    margin={{ top: 0, right: 8, bottom: 0, left: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) =>
                        activeCurrency === "USD"
                          ? `U$D ${(v / 1000).toFixed(0)}k`
                          : `$${(v / 1000).toFixed(0)}k`
                      }
                    />
                    <YAxis
                      type="category"
                      dataKey="nombre"
                      width={120}
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => (v.length > 18 ? v.slice(0, 17) + "…" : v)}
                    />
                    <Tooltip content={<CustomTooltip currency={activeCurrency} />} />
                    <Bar
                      dataKey="monto"
                      name="Monto Neto"
                      fill="#818cf8"
                      radius={[0, 3, 3, 0]}
                      maxBarSize={22}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Funnel de conversión */}
            <div className="card rounded-xl p-4 border border-white/10">
              <h2 className="text-sm font-semibold text-foreground mb-4">
                Funnel de Conversión
              </h2>
              <div className="space-y-3">
                {funnel.map((step) => (
                  <div key={step.label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-foreground font-medium">{step.label}</span>
                      <span className="text-muted font-mono">
                        {fmt(step.count)} ({fmt(step.pct, 1)}%)
                      </span>
                    </div>
                    <div className="h-5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.max(step.pct, 2)}%`, background: step.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ─── Tabla resumen por cliente ─── */}
          <div className="card rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground">
                Resumen por Cliente
                {activeCurrency && (
                  <span className="ml-2 text-xs font-normal text-muted">({activeCurrency})</span>
                )}
              </h2>
              {mixedCurrencies && (
                <span className="text-xs text-amber-400">⚠️ Montos en monedas distintas</span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted border-b border-white/10">
                    <th className="text-left py-2 pr-4 font-medium">Cliente</th>
                    <th className="text-right py-2 pr-4 font-medium">Ofertas</th>
                    <th className="text-right py-2 pr-4 font-medium">Monto Neto</th>
                    <th className="text-right py-2 pr-4 font-medium">Promedio</th>
                    <th className="text-right py-2 font-medium">% del total</th>
                  </tr>
                </thead>
                <tbody>
                  {topClients.map((c, i) => {
                    const pct = kpis.totalAmount > 0 ? (c.monto / kpis.totalAmount) * 100 : 0;
                    return (
                      <tr
                        key={c.nombre}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="py-2 pr-4 text-foreground">
                          <span className="mr-2 text-muted font-mono">{i + 1}.</span>
                          {c.nombre}
                        </td>
                        <td className="py-2 pr-4 text-right text-muted font-mono">{fmt(c.cantidad)}</td>
                        <td className="py-2 pr-4 text-right font-mono text-emerald-400">
                          {mixedCurrencies ? `$${fmt(c.monto, 0)}` : fmtMoney(c.monto, activeCurrency)}
                        </td>
                        <td className="py-2 pr-4 text-right text-muted font-mono">
                          {mixedCurrencies ? "—" : fmtMoney(c.monto / c.cantidad, activeCurrency)}
                        </td>
                        <td className="py-2 text-right text-amber-400 font-mono">
                          {mixedCurrencies ? "—" : `${fmt(pct, 1)}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
